
import { supabase } from "@/integrations/supabase/client";

// Email administrateur masqué dans une constante
export const ADMIN_EMAIL = "gpa@ledlr.fr";

export const loginWithPassword = async (password: string): Promise<boolean> => {
  try {
    console.log("Attempting login");
    const { error } = await supabase.auth.signInWithPassword({
      email: ADMIN_EMAIL,
      password,
    });

    if (error) {
      console.error("Login error:", error.message);
      return false;
    }
    return true;
  } catch (error) {
    console.error("Unexpected error:", error);
    return false;
  }
};

export const requestPasswordReset = async (): Promise<boolean> => {
  try {
    console.log("Requesting password reset");
    
    const baseUrl = window.location.origin;
    const isLocalhost = baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1');
    const isPreviewEnv = baseUrl.includes('lovable.app') || baseUrl.includes('preview');
    
    // Always use hash routing for preview and production environments
    const useHashRouter = !isLocalhost;
    const hashPrefix = useHashRouter ? '#' : '';
    const redirectPath = `${hashPrefix}/reset-password`;
    const redirectUrl = `${baseUrl}${redirectPath}`;
    
    console.log("Reset password request details:", {
      origin: window.location.origin,
      hostname: window.location.hostname,
      redirectTo: redirectUrl,
      hashPrefix,
      redirectPath,
      isLocalhost,
      isPreviewEnv,
      useHashRouter,
      timestamp: new Date().toISOString()
    });
    
    // Generate a reset token 
    const resetToken = crypto.randomUUID();
    
    // Call our custom password reset function with extended debugging
    console.log("Sending custom password reset email via edge function with token:", 
      resetToken.substring(0, 6) + "...");
      
    const response = await supabase.functions.invoke('send-password-reset', {
      body: {
        email: ADMIN_EMAIL,
        resetToken: resetToken,
        resetUrl: redirectUrl,
        debug: {
          environment: isLocalhost ? 'local' : (isPreviewEnv ? 'preview' : 'production'),
          origin: window.location.origin,
          useHashRouter: useHashRouter,
          fullUrl: window.location.href,
          hostname: window.location.hostname
        }
      }
    });
    
    console.log("Custom email function response:", response);
    
    if (response.error) {
      console.error("Error from custom email function:", response.error);
      return false;
    }

    // Store token temporarily to validate it later
    try {
      // Save token in localStorage for validation (5 min expiry)
      localStorage.setItem('reset_token', resetToken);
      localStorage.setItem('reset_token_expiry', (Date.now() + 30 * 60 * 1000).toString()); // 30 min
      console.log("Reset token saved locally for validation");
    } catch (e) {
      console.warn("Could not save token locally:", e);
    }

    console.log("Password reset email successfully requested");
    return true;
  } catch (error) {
    console.error("Error requesting password reset:", error);
    return false;
  }
};

export const resetPassword = async (token: string, newPassword: string): Promise<boolean> => {
  try {
    console.log("Resetting password with token:", token ? token.substring(0, 6) + "..." : "null");
    
    if (!token) {
      console.error("No token provided for password reset");
      return false;
    }
    
    // Enhanced token validation and password reset
    // 1. First try the admin reset approach
    console.log("Trying admin reset first");
    try {
      const { data, error } = await supabase.functions.invoke('admin-reset-password', {
        body: {
          email: ADMIN_EMAIL,
          newPassword: newPassword,
          resetToken: token
        }
      });
      
      if (!error) {
        console.log("Password successfully reset via admin function", data);
        localStorage.removeItem('reset_token');
        localStorage.removeItem('reset_token_expiry');
        return true;
      } else {
        console.error("Admin reset error:", error);
      }
    } catch (adminError) {
      console.error("Error with admin reset:", adminError);
    }
    
    // 2. Try local token validation
    const storedToken = localStorage.getItem('reset_token');
    const tokenExpiry = localStorage.getItem('reset_token_expiry');
    
    if (storedToken && tokenExpiry && Date.now() <= parseInt(tokenExpiry)) {
      if (storedToken === token) {
        console.log("Token validated against locally stored token");
        
        // 3. Try using the update user approach
        try {
          const updateResult = await supabase.auth.updateUser({
            password: newPassword
          });
          
          if (!updateResult.error) {
            console.log("Password updated successfully through updateUser");
            localStorage.removeItem('reset_token');
            localStorage.removeItem('reset_token_expiry');
            return true;
          }
          console.error("Error updating password via updateUser:", updateResult.error);
        } catch (updateError) {
          console.error("Exception during updateUser:", updateError);
        }
      }
    }
    
    // 4. As a fallback, try sign in with token as OTP
    console.log("Attempting to verify token as OTP");
    try {
      const { error } = await supabase.auth.verifyOtp({
        email: ADMIN_EMAIL,
        token: token,
        type: 'recovery'
      });
      
      if (!error) {
        console.log("OTP verification successful");
        
        // Now update the password
        const updateResult = await supabase.auth.updateUser({
          password: newPassword
        });
        
        if (!updateResult.error) {
          console.log("Password update successful after OTP verification");
          return true;
        }
        
        console.error("Failed to update password after OTP verification:", updateResult.error);
      } else {
        console.error("OTP verification failed:", error);
      }
    } catch (otpError) {
      console.error("Error during OTP verification:", otpError);
    }
    
    // 5. Last resort - try the recovery flow from Supabase directly
    console.log("Trying Supabase recovery flow as last resort");
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(ADMIN_EMAIL, {
        redirectTo: `${window.location.origin}/#/reset-password`
      });
      
      if (!error) {
        console.log("Password reset email sent through Supabase");
        return false; // Return false as we need the user to click the new link
      }
      
      console.error("Supabase reset password email failed:", error);
    } catch (resetError) {
      console.error("Error during Supabase reset:", resetError);
    }
    
    console.error("All password reset methods failed");
    return false;
  } catch (error) {
    console.error("Unexpected error during password reset:", error);
    return false;
  }
};
